import React from 'react';
import { useTranslation } from 'react-i18next';

function AssignmentPreview({ formData, onRegenerate }) {
  const { t } = useTranslation();
  const { generatedAssignments, participants, showOnlyGivers, eventType, violationsByType, hasViolations } = formData;

  if (!generatedAssignments) {
    return (
      <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
        {t('assignmentPreview.noAssignments')}
      </div>
    );
  }

  const givers = (eventType === 'kris-kindle' && showOnlyGivers)
    ? participants.filter(p => p.isGiver)
    : participants;

  // Sort givers alphabetically
  const sortedGivers = [...givers].sort((a, b) => a.name.localeCompare(b.name));

  // Get participant details by name
  const getParticipant = (name) => {
    return participants.find(p => p.name === name);
  };

  // Helper to check if a giver-receiver pair has reciprocal violation
  const hasReciprocalViolation = (giverName, receiverName) => {
    if (!violationsByType || !violationsByType.reciprocal) return false;
    return violationsByType.reciprocal.some(
      v => v.gifter === giverName && v.receiver === receiverName
    );
  };

  // Helper to check if a giver has family multi-buy violation
  const hasFamilyViolation = (giverName) => {
    if (!violationsByType || !violationsByType.family) return false;
    return violationsByType.family.some(v => v.gifter === giverName);
  };

  // Helper to check if a giver has own-family violation (CRITICAL)
  const hasOwnFamilyViolation = (giverName) => {
    if (!violationsByType || !violationsByType.ownFamily) return false;
    return violationsByType.ownFamily.some(v => v.gifter === giverName);
  };

  // Helper to get family violations for a specific giver
  const getFamilyViolations = (giverName) => {
    if (!violationsByType || !violationsByType.family) return [];
    return violationsByType.family.filter(v => v.gifter === giverName);
  };

  // Helper to get own-family violations for a specific giver
  const getOwnFamilyViolations = (giverName) => {
    if (!violationsByType || !violationsByType.ownFamily) return [];
    return violationsByType.ownFamily.filter(v => v.gifter === giverName);
  };

  // Helper to check if a giver has adult violation
  const hasAdultViolation = (giverName) => {
    if (!violationsByType || !violationsByType.adult) return false;
    return violationsByType.adult.some(v => v.gifter === giverName);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">{t('assignmentPreview.summary.title')}</h3>
            <p className="text-blue-800">
              {t('assignmentPreview.summary.description')}
            </p>
          </div>
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              className="ml-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
            >
              <span>🔄</span>
              <span>{t('assignmentPreview.summary.regenerateButton')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Show violation summary banner if any violations exist */}
      {hasViolations && violationsByType && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-6">
          <h3 className="font-bold text-amber-900 mb-2 flex items-center">
            <span className="text-xl mr-2">⚠️</span>
            Rule Violations Detected
          </h3>
          <p className="text-sm text-amber-800 mb-3">
            This assignment has the following rule violations. Review the details below:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {violationsByType.ownFamily && violationsByType.ownFamily.length > 0 && (
              <div className="bg-red-100 p-2 rounded border-2 border-red-500">
                <div className="text-2xl font-bold text-red-900">{violationsByType.ownFamily.length}</div>
                <div className="text-xs text-red-800">🚨 Same Family</div>
              </div>
            )}
            {violationsByType.adult && violationsByType.adult.length > 0 && (
              <div className="bg-red-100 p-2 rounded">
                <div className="text-2xl font-bold text-red-900">{violationsByType.adult.length}</div>
                <div className="text-xs text-red-800">🎯 Adult Rule</div>
              </div>
            )}
            {violationsByType.reciprocal && violationsByType.reciprocal.length > 0 && (
              <div className="bg-orange-100 p-2 rounded">
                <div className="text-2xl font-bold text-orange-900">{violationsByType.reciprocal.length}</div>
                <div className="text-xs text-orange-800">🔄 Reciprocal</div>
              </div>
            )}
            {violationsByType.family && violationsByType.family.length > 0 && (
              <div className="bg-yellow-100 p-2 rounded">
                <div className="text-2xl font-bold text-yellow-900">{violationsByType.family.length}</div>
                <div className="text-xs text-yellow-800">👨‍👩‍👧‍👦 Multi-Family</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {sortedGivers.map(giver => {
          const giverName = giver.name;
          const receiverNames = generatedAssignments[giverName] || [];
          const hasReciprocal = receiverNames.some(r => hasReciprocalViolation(giverName, r));
          const hasFamily = hasFamilyViolation(giverName);
          const hasOwnFamily = hasOwnFamilyViolation(giverName);
          const hasAdult = hasAdultViolation(giverName);
          const familyViolations = getFamilyViolations(giverName);
          const ownFamilyViolations = getOwnFamilyViolations(giverName);
          const hasViolations = hasReciprocal || hasFamily || hasOwnFamily || hasAdult;

          return (
            <div
              key={giverName}
              className={`rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${
                hasViolations
                  ? 'bg-yellow-50 border-2 border-yellow-400'
                  : 'bg-white border border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg mr-3">
                      {giverName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-gray-800">{giverName}</h3>
                        {hasOwnFamily && (
                          <span className="text-xs bg-red-600 text-white px-2 py-1 rounded-full font-bold border-2 border-red-700">
                            🚨 SAME FAMILY
                          </span>
                        )}
                        {hasAdult && (
                          <span className="text-xs bg-red-200 text-red-900 px-2 py-1 rounded-full font-semibold">
                            🎯 Adult Rule
                          </span>
                        )}
                        {hasReciprocal && (
                          <span className="text-xs bg-orange-200 text-orange-900 px-2 py-1 rounded-full font-semibold">
                            🔄 Reciprocal
                          </span>
                        )}
                        {hasFamily && (
                          <span className="text-xs bg-yellow-200 text-yellow-900 px-2 py-1 rounded-full font-semibold">
                            👨‍👩‍👧‍👦 Multi-Family
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {receiverNames.length === 1
                          ? t('assignmentPreview.giftsToPurchase.singular')
                          : t('assignmentPreview.giftsToPurchase.plural', { count: receiverNames.length })
                        }
                      </p>

                      {/* Violation Details */}
                      {ownFamilyViolations.length > 0 && (
                        <div className="text-xs bg-red-200 border-2 border-red-600 text-red-900 mt-2 p-2 rounded font-semibold">
                          🚨 <strong>CRITICAL: Same Family Violation</strong><br/>
                          This person is assigned to buy for their own family member(s):
                          <ul className="ml-4 mt-1">
                            {ownFamilyViolations.map((v, idx) => (
                              <li key={idx}>• {v.gifter} → {v.receiver}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {hasAdult && (
                        <div className="text-xs bg-red-100 border border-red-300 text-red-900 mt-2 p-2 rounded">
                          ⚠️ <strong>Adult Rule Violation:</strong> This adult is not buying for any other adult
                        </div>
                      )}
                      {familyViolations.length > 0 && (
                        <div className="text-xs bg-yellow-100 border border-yellow-300 text-yellow-900 mt-2 p-2 rounded">
                          ⚠️ <strong>Multi-Family Violation:</strong> Buying for multiple people in same family
                          <ul className="ml-4 mt-1">
                            {familyViolations.map((v, idx) => (
                              <li key={idx}>• {v.gifter} → {v.receiver}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="ml-15 space-y-2">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('assignmentPreview.buyingFor')}</h4>
                    {receiverNames.map((receiverName, index) => {
                      const receiver = getParticipant(receiverName);
                      const isReciprocal = hasReciprocalViolation(giverName, receiverName);
                      return (
                        <div
                          key={index}
                          className={`flex items-center p-3 rounded-md border ${
                            isReciprocal
                              ? 'bg-yellow-100 border-yellow-400 border-2'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-sm mr-3">
                            {receiverName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-800">{receiverName}</span>
                              {isReciprocal && (
                                <span className="text-xs bg-orange-300 text-orange-900 px-2 py-1 rounded font-semibold">
                                  🔄 Reciprocal: {receiverName} is also buying for {giverName}
                                </span>
                              )}
                            </div>
                            {receiver && (
                              <div className="text-xs text-gray-600 mt-1">
                                {receiver.age && <span className="mr-2">{t('assignmentPreview.recipientDetails.age')} {receiver.age}</span>}
                                {receiver.sex && <span className="mr-2">{t('assignmentPreview.recipientDetails.sex')} {receiver.sex}</span>}
                                {receiver.preferences && (
                                  <span className="block mt-1 italic">{t('assignmentPreview.recipientDetails.preferences')} {receiver.preferences}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="text-2xl">🎁</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-semibold text-green-900 mb-2">{t('assignmentPreview.complete.title')}</h3>
        <p className="text-green-800 text-sm">
          {t('assignmentPreview.complete.description')}
        </p>
      </div>
    </div>
  );
}

export default AssignmentPreview;
